import React from "react";
import Navigation from "./Navigation";
import Dropdown from "./Dropdown";
import { t, LANGUAGES, getLanguage, setLanguage } from "../i18n";
import { THEMES, THEME_MODES, PALETTES, setTheme, setThemeMode, setPalette, resolvePalette, themeModes, useThemeState } from "../theme";
import { ThemeMark } from "./ThemeLogo";
import PageHead from "./PageHead";

// Aperçu d'un mode : une maquette miniature plutôt qu'une pastille de couleur —
// on voit tout de suite ce qu'on choisit. « auto » montre les deux moitiés.
function ModePreview({ mode }) {
  return (
    <span class={`thm-preview thm-preview-${mode}`} aria-hidden="true">
      <span class="thm-preview-half light">
        <span class="thm-pill" /><span class="thm-line" /><span class="thm-line short" />
      </span>
      <span class="thm-preview-half dark">
        <span class="thm-pill" /><span class="thm-line" /><span class="thm-line short" />
      </span>
    </span>
  );
}

// Icônes de section : les seules du jeu hors admin, reprises de la maquette des
// réglages — elles titrent une section, elles ne décorent pas un bouton.
function MoonGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" fill="currentColor" />
      <path d="M17.5 3l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8L15 5.5l1.8-.7z" fill="currentColor" />
    </svg>
  );
}
function PaletteGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M12 3a9 9 0 0 0 0 18c1.3 0 2-.8 2-1.8 0-.5-.2-.9-.5-1.2-.3-.4-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8H16a5 5 0 0 0 5-5c0-3.9-4-7-9-7z"
        fill="none" stroke="currentColor" stroke-width="1.8" />
      <circle cx="7.5" cy="12" r="1.3" fill="currentColor" />
      <circle cx="9.8" cy="8.2" r="1.3" fill="currentColor" />
      <circle cx="14.2" cy="7.6" r="1.3" fill="currentColor" />
      <circle cx="17.4" cy="10.4" r="1.3" fill="currentColor" />
    </svg>
  );
}
function LanguageGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M3 5h9M7.5 5v1.6c0 3.3-1.9 6.3-4.5 7.9M6 9.4c.9 2.2 2.7 4 5 4.8"
        fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M12.5 20l4-10 4 10M14 17h5" fill="none" stroke="currentColor" stroke-width="1.8"
        stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

function Settings({ account, player, hasOngoingGame, inGame, isHost, isAdmin, isSuperAdmin, ownedThemes, ownedPalettes,
  onProfileClick, onShopClick, onAdminClick, onSystemClick, onLogout, onLeaveGame, onCloseLobby, onBack }) {
  const { mode, theme, palettes } = useThemeState();
  // Un habillage vendu ne se choisit qu'une fois acheté ; les deux PopuQuiz
  // n'ont pas d'objet et restent offerts.
  const owned = new Set(ownedThemes || []);
  const available = THEMES.filter(th => !th.item || owned.has(th.id));
  const toBuy = THEMES.filter(th => th.item && !owned.has(th.id));

  // Palettes du thème actuel. La section reste à sa place même quand
  // l'habillage n'en propose qu'une : une section qui disparaît fait sauter la
  // colonne d'un écran à l'autre, et on ne sait plus qu'elle existe.
  const themePalettes = PALETTES[theme] || [];
  const hasPalettes = themePalettes.length > 1;
  const ownedPaletteSet = new Set(ownedPalettes || []);
  const activePaletteId = resolvePalette(theme, palettes);
  // Les modes que l'habillage (ou sa palette) sait tenir : les autres se grisent.
  const allowedModes = themeModes(theme, activePaletteId);

  return (
    <div class="container">
      <Navigation
        player={player}
        account={account}
        hasOngoingGame={hasOngoingGame}
        inGame={inGame}
        isHost={isHost}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        onProfileClick={onProfileClick}
        onSettingsClick={undefined}
        onShopClick={onShopClick}
        onAdminClick={onAdminClick}
        onSystemClick={onSystemClick}
        onLogout={onLogout}
        onLeaveGame={onLeaveGame}
        onCloseLobby={onCloseLobby}
      />
      <PageHead onBack={onBack} title={t("settings.title")} />
      <div class="Home settings-page">
        <div class="settings-list">

          {/* Deux colonnes en grand écran : les réglages courts à gauche, la
              grille d'habillages — la seule qui ait besoin de place — à droite.
              En dessous de 900 px la grille retombe en une seule colonne. */}
          <div class="settings-cols">
          <div class="settings-col">

          {/* Langue */}
          <section class="settings-card row">
            <div class="settings-card-head">
              <span class="settings-card-icon"><LanguageGlyph /></span>
              <h3 class="settings-card-title">{t("profile.language")}</h3>
            </div>
            <Dropdown
              value={getLanguage()}
              ariaLabel={t("profile.language")}
              options={LANGUAGES.map(l => ({ value: l.code, label: l.label }))}
              onChange={setLanguage}
            />
          </section>

          {/* Mode d'affichage */}
          <section class="settings-card">
            <div class="settings-card-head">
              <span class="settings-card-icon"><MoonGlyph /></span>
              <h3 class="settings-card-title">{t("settings.themeMode")}</h3>
            </div>
            <div class="thm-modes">
              {THEME_MODES.map(m => {
                const off = !allowedModes.includes(m);
                return (
                  <button key={m} type="button" disabled={off}
                    class={`thm-tile${mode === m && !off ? " active" : ""}${off ? " off" : ""}`}
                    aria-pressed={mode === m && !off}
                    title={off ? t("settings.modeUnavailable") : undefined}
                    onClick={() => setThemeMode(m)}>
                    <ModePreview mode={m} />
                    <span class="thm-tile-label">{t(`settings.mode_${m}`)}</span>
                  </button>
                );
              })}
            </div>
            {allowedModes.length < THEME_MODES.length && (
              <p class="settings-note">{t(allowedModes.length ? "settings.modeLocked" : "settings.modeNone")}</p>
            )}
          </section>

          {/* Palette : la section reste, éteinte, pour les habillages à couleur
              unique — on voit ainsi qu'elle existe et à quoi elle sert. */}
          {(
            <section class={`settings-card${hasPalettes ? "" : " disabled"}`}>
              <div class="settings-card-head">
                <span class="settings-card-icon"><PaletteGlyph /></span>
                <h3 class="settings-card-title">{t("settings.palette")}</h3>
              </div>
              {!hasPalettes && <p class="settings-note">{t("settings.paletteNone")}</p>}
              <div class="thm-themes">
                {themePalettes.map(p => {
                  const locked = p.item && !ownedPaletteSet.has(p.item);
                  return (
                    <button key={p.id} type="button"
                      class={`thm-swatch${activePaletteId === p.id ? " active" : ""}${locked ? " locked" : ""}`}
                      aria-pressed={activePaletteId === p.id}
                      onClick={() => locked ? onShopClick && onShopClick() : setPalette(theme, p.id)}>
                      <span class="thm-swatch-demo" aria-hidden="true">
                        {locked
                          ? <span class="thm-swatch-soon">{t("settings.paletteBuy")}</span>
                          : <span class="shop-preview-palette">{p.swatch.map((c, i) => <span key={i} style={{ background: c }} />)}</span>}
                      </span>
                      <span class="thm-swatch-name">{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          </div>
          <div class="settings-col">

          {/* Habillage */}
          <section class="settings-card">
            <div class="settings-card-head">
              <span class="settings-card-icon"><PaletteGlyph /></span>
              <h3 class="settings-card-title">{t("settings.theme")}</h3>
            </div>
            <div class="thm-themes">
              {available.map(th => (
                <button key={th.id} type="button"
                  class={`thm-swatch${theme === th.id ? " active" : ""}`}
                  aria-pressed={theme === th.id}
                  onClick={() => setTheme(th.id)}>
                  <span class={`thm-swatch-demo theme-demo-${th.id}`} aria-hidden="true">
                    {/* Chaque habillage se présente par son propre lettrage ;
                        ceux qui n'en ont pas encore gardent leur PNG ou leur
                        nom — jamais le logo d'un voisin. */}
                    {th.logo
                      ? <img class="thm-swatch-logo" src={th.logo} alt="" />
                      : <ThemeMark theme={th.id} name={th.name} />}
                  </span>
                  <span class="thm-swatch-name">{th.name}</span>
                </button>
              ))}
              {/* Livrés mais pas achetés : la case renvoie à la boutique — sauf
                  un secret (Backrooms), qui ne s'y trouve pas et ne dit rien
                  de plus qu'un point d'interrogation. */}
              {toBuy.map(th => (
                <button key={th.id} type="button" class="thm-swatch locked"
                  onClick={th.secret ? undefined : onShopClick}>
                  <span class={`thm-swatch-demo theme-demo-${th.id}`} aria-hidden="true">
                    <span class="thm-swatch-soon">{th.secret ? "?" : t("settings.themeBuy")}</span>
                  </span>
                  <span class="thm-swatch-name">{th.name}</span>
                </button>
              ))}
            </div>
          </section>

          </div>
          </div>

          <p class="settings-more">{t("settings.more")}</p>
        </div>
      </div>
    </div>
  );
}

export default Settings;
